'use client';

import { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/lib/auth/context';
import CaseResultCard from './CaseResultCard';
import SearchProgress from './SearchProgress';
import SearchMethodSelector from './SearchMethodSelector';
import type { SmartFinderResponse, CaseResult, SearchIntent, StructuredSearchRequest } from '@/lib/smart-finder/types';
import { analyticsEvents, trackEvent } from '@/components/analytics/Analytics';
import { buildTrackCaseParams } from './track-case-handoff';
import {
  classifyEmptySearchAnalytics,
  isIncompleteSearchResponse,
} from '@/lib/smart-finder/response-classification';

export { buildTrackCaseParams } from './track-case-handoff';

export const SMART_FINDER_EXAMPLE_QUERIES = [
  'DLHC010012342024',
  'Rahul Sharma Delhi District Court 2024',
  'WP/2636/2025 Delhi High Court',
] as const;

export const INDEX_MATCH_NOTICE = 'More cases may match. Sign in to find yours.';
export const AUTHENTICATED_INDEX_MATCH_NOTICE = 'Found in recent official records. Add this case to verify its latest status, hearings, orders, and CNR.';
export const PUBLIC_RESULT_PREVIEW_COUNT = 2;
export const SMART_FINDER_SEARCH_REQUIREMENTS = 'Case-number searches need the case type, number, filing year, and court. Party searches need the party name and court or city.';
export const INCOMPLETE_SEARCH_MESSAGE = 'The court search did not finish checking every requested court. This is not a confirmed no-result. Please try again.';

/** Classify coverage gaps independently of the response shape used during a rolling deploy. */
export { classifyEmptySearchAnalytics, isIncompleteSearchResponse };

/**
 * A case-number search that bounced only because the type code is missing.
 * Litigants usually do not know their type code, so a text-only ask is a dead
 * end (prod: one visitor retried "2079/2025 Kanpur" three times and left) —
 * offer a type input for those with papers and a party-name pivot for the rest.
 */
export function caseTypeRecoveryFromResponse(
  data: SmartFinderResponse,
): { caseNumber: string; caseYear: string | null } | null {
  if (
    data.type === 'clarification'
    && data.failureCode === 'INVALID_SCOPE'
    && data.parsed?.search_strategy === 'case_number'
    && data.parsed.case_number
    && !data.parsed.case_type
  ) {
    return { caseNumber: data.parsed.case_number, caseYear: data.parsed.case_year || null };
  }
  return null;
}

/** Distinguish the positive identity-index fast path from partial live-provider results. */
export function isRecentIndexMatch(data: SmartFinderResponse): boolean {
  const internalCacheWasUsed = data.providers?.includes('internal_cache')
    || data.providersAttempted?.includes('internal_cache');
  return data.type === 'results'
    && Boolean(data.cases?.length)
    && data.complete === false
    && data.refreshStatus === 'stale'
    && Boolean(internalCacheWasUsed);
}

export function buildSmartFinderRequestBody(input: {
  query: string;
  v2?: StructuredSearchRequest;
  logicalSearchId?: string | null;
  courtContext?: string;
  sourceSurface: string;
  sourceSlug?: string;
  /** Escape hatch from the candidate-chooser notice: re-run against the full court scope. */
  skipCandidates?: boolean;
}): Record<string, unknown> {
  return {
    // Legacy mode keeps its compatibility behaviour. Structured mode keeps
    // visitor input and the server-resolved court scope separate.
    query: input.v2 || input.sourceSlug
      ? input.query
      : input.courtContext
        ? `${input.query} ${input.courtContext}`
        : input.query,
    ...(input.v2 ? { v2: input.v2 } : {}),
    // Both modes carry the logical-search id so a clarification and the
    // visitor's refined retry count as one journey in search telemetry.
    ...(input.logicalSearchId ? { logicalSearchId: input.logicalSearchId } : {}),
    source_surface: input.sourceSurface,
    ...(input.sourceSlug ? { source_slug: input.sourceSlug } : {}),
    ...(input.skipCandidates ? { skip_candidates: true } : {}),
  };
}

/**
 * Results rendered from stored-record candidates (case-number, yearless, or
 * party-name exact matches) are verified live one by one, but the candidate
 * list itself is never exhaustive. Tell the visitor what kind of match this
 * was and offer the matching escape hatch — never imply "these are all the
 * cases".
 */
export function candidateChooserNotice(
  data: SmartFinderResponse,
): { message: string; escape: 'case_type' | 'case_year' | 'full_search' } | null {
  if (data.type !== 'results') return null;
  if (!data.cases || data.cases.length < 1) return null;
  const resolution = data.candidateResolution;
  if (!resolution) return null;

  const n = data.cases.length;
  const s = n === 1 ? '' : 's';
  let message: string;
  if (resolution.matchedBy === 'case_number') {
    message = n === 1
      ? 'We found a case matching that number and year in this court’s official records — verified live just now.'
      : `We found ${n} case${s} matching that number and year in this court’s official records — each verified live just now. Open yours below.`;
  } else if (resolution.matchedBy === 'case_number_yearless') {
    message = n === 1
      ? 'We found a case matching that case number across recent years in this court’s official records — verified live just now.'
      : `We found ${n} case${s} matching that case number across recent years in this court’s official records — each verified live just now. Open yours below.`;
  } else {
    message = `We found ${n} case${s} under that exact party name in official court records — verified live just now.`;
  }
  if (resolution.truncated) {
    message += ' More matches may exist.';
  }
  return { message, escape: resolution.escape };
}

interface SmartCaseFinderProps {
  // Render the non-authenticated "masked + sign-in gate" variant even when the
  // user is logged in — used on the demo dashboard so unsubscribed users see
  // "we found N cases, upgrade to view" instead of the full list for free.
  demoMode?: boolean;
  /** If provided, clicking "Track" on a result calls this instead of routing.
   *  Used by the ad LP flow so the paywall modal can open in place. */
  onTrackClick?: (caseResult: CaseResult, searchEventId?: string | null) => void;
  /** Override the pre-search placeholder. Default cycles through example queries. */
  placeholder?: string;
  /** Prefill a preserved search intent, such as a login redirect to /add-case. */
  initialQuery?: string;
  /** Court/location context appended to the query sent to the API (not shown
   *  in the input) — used on court SEO pages so searches stay scoped. */
  courtContext?: string;
  /** SEO slug for the seo_landing_search_submitted analytics event. */
  searchSlug?: string;
  /** Analytics surface label paired with searchSlug (e.g. "location_page"). */
  searchSurface?: string;
  /** Server-controlled rollout state. Defaults off until `search_v2` is enabled. */
  searchV2Enabled?: boolean;
  /** Case number and party modes can follow the lower-risk CNR rollout. */
  searchV2Modes?: { caseNumber: boolean; partyName: boolean };
  /** Canonical scope is resolved server-side from this public SEO slug. */
  sourcePageSlug?: string;
  /** Visible scope label; it is never appended to visitor input. */
  pageScopeLabel?: string;
  /** Human-readable city/location used to restate an All Courts choice. */
  pageLocationLabel?: string;
  /** All Courts pages require an explicit tier choice before provider work. */
  pageCourtType?: 'District Court' | 'High Court' | 'All Courts' | 'Supreme Court';
  /** Nationwide MACT/Family Court pages need a city, never a complex choice. */
  requiresCourtLocation?: boolean;
}

export default function SmartCaseFinder({
  demoMode = false,
  onTrackClick,
  placeholder,
  initialQuery = '',
  courtContext,
  searchSlug,
  searchSurface,
  searchV2Enabled = false,
  searchV2Modes = { caseNumber: true, partyName: true },
  sourcePageSlug,
  pageScopeLabel,
  pageLocationLabel,
  pageCourtType,
  requiresCourtLocation = false,
}: SmartCaseFinderProps) {
  const router = useRouter();
  const { isAuthenticated: isActuallyAuthenticated } = useAuth();
  const showFullResults = isActuallyAuthenticated && !demoMode;
  const showLimitedPreview = demoMode && isActuallyAuthenticated;
  const [input, setInput] = useState(initialQuery);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<CaseResult[]>([]);
  const [freeSearch, setFreeSearch] = useState<SmartFinderResponse['freeSearch'] | null>(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [correctedMessage, setCorrectedMessage] = useState('');
  const [messageType, setMessageType] = useState<'info' | 'error'>('info');
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [searchEventId, setSearchEventId] = useState<string | null>(null);
  const [logicalSearchId, setLogicalSearchId] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [activeExample, setActiveExample] = useState(0);
  const [showRetryButton, setShowRetryButton] = useState(false);
  const [showSignInPrompt, setShowSignInPrompt] = useState(false);
  const [isIndexedMatch, setIsIndexedMatch] = useState(false);
  const [caseTypeRecovery, setCaseTypeRecovery] = useState<{ caseNumber: string; caseYear: string | null } | null>(null);
  const [candidateEscapeState, setCandidateEscapeState] = useState<{
    escape: 'case_type' | 'case_year' | 'full_search';
    caseNumber: string;
    caseYear: string | null;
    caseType: string | null;
  } | null>(null);
  const [recoveryCaseType, setRecoveryCaseType] = useState('');
  const [recoveryPartyName, setRecoveryPartyName] = useState('');
  const [caseYearRecoveryOpen, setCaseYearRecoveryOpen] = useState(false);
  const [recoveryCaseYear, setRecoveryCaseYear] = useState('');
  const [intent, setIntent] = useState<SearchIntent>('cnr');
  const [caseType, setCaseType] = useState('');
  const [caseNumber, setCaseNumber] = useState('');
  const [caseYear, setCaseYear] = useState('');
  const [partyName, setPartyName] = useState('');
  const [courtLocation, setCourtLocation] = useState('');
  const [allCourtsTier, setAllCourtsTier] = useState<'high_court' | 'district' | ''>('');
  const needsExplicitTier = intent !== 'cnr'
    && !requiresCourtLocation
    && (pageCourtType === 'All Courts' || !sourcePageSlug);
  const logicalSearchIdRef = useRef<string | null>(null);
  const lastQueryRef = useRef('');
  const feedbackRef = useRef<HTMLDivElement>(null);
  const [retryNotice, setRetryNotice] = useState('');
  const lastV2RequestRef = useRef<StructuredSearchRequest | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const quickSearchTips = sourcePageSlug && !requiresCourtLocation
    ? [
        { label: 'Party Name', example: 'Rahul Sharma 2024' },
        { label: 'Case Number', example: 'WP/2636/2025' },
        { label: 'CNR', example: 'DLHC010012342024' },
      ]
    : [
        { label: 'Party Name', example: 'Rahul Sharma Delhi District Court 2024' },
        { label: 'Case Number', example: 'WP/2636/2025 Delhi HC' },
        { label: 'CNR', example: 'DLHC010012342024' },
      ];

  // Cycle through example queries in placeholder
  useEffect(() => {
    if (hasSearched) return;
    const interval = setInterval(() => {
      setActiveExample((prev) => (prev + 1) % SMART_FINDER_EXAMPLE_QUERIES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [hasSearched]);

  useEffect(() => {
    if (!hasSearched && initialQuery) setInput(initialQuery);
  }, [hasSearched, initialQuery]);

  // Scroll results into view
  useEffect(() => {
    if (results.length > 0) {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [results]);

  // Keep the search feedback visible: the progress panel (with its elapsed
  // timer) renders below the fold on the hero surfaces, so without this a
  // 20–40s search shows nothing but the button spinner — and a below-fold
  // error message is never seen at all.
  useEffect(() => {
    if (isLoading || (statusMessage && results.length === 0)) {
      feedbackRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isLoading, statusMessage, results.length]);

  useEffect(() => {
    if (!demoMode || !isActuallyAuthenticated) return;
    let cancelled = false;
    fetch('/api/free-search/quota')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && data) {
          setFreeSearch({
            quotaLimit: Number(data.quotaLimit || 2),
            searchesUsed: Number(data.searchesUsed || 0),
            remaining: Number(data.remaining || 0),
          });
        }
      })
      .catch(() => {
        // Quota display is helpful but not required; the API still enforces it.
      });
    return () => { cancelled = true; };
  }, [demoMode, isActuallyAuthenticated]);

  useEffect(() => {
    const onPageHide = () => {
      if (!isLoading || !logicalSearchIdRef.current) return;
      const payload = JSON.stringify({ event_name: 'search_abandoned', logical_search_id: logicalSearchIdRef.current });
      navigator.sendBeacon?.('/api/search-funnel/event', new Blob([payload], { type: 'application/json' }));
    };
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, [isLoading]);

  const quotaLabel = freeSearch
    ? freeSearch.remaining <= 0
      ? 'No free searches left'
      : `${freeSearch.remaining} free ${freeSearch.remaining === 1 ? 'search' : 'searches'} left`
    : null;

  const logResultClick = (caseResult: CaseResult) => {
    if (!searchEventId) return;
    fetch('/api/search-funnel/event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        event_name: 'result_clicked',
        search_event_id: searchEventId,
        search_type: 'smart_finder',
        source: demoMode ? 'demo_search_result' : 'smart_case_finder',
        case_payload: caseResult,
      }),
    }).catch(() => {
      // Search funnel telemetry should never block navigation.
    });
  };

  const logSignInStarted = () => {
    if (!logicalSearchIdRef.current && !searchEventId) return;
    fetch('/api/search-funnel/event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        event_name: 'sign_in_started',
        search_event_id: searchEventId,
        logical_search_id: logicalSearchIdRef.current,
        source: 'track_case_cta',
      }),
    }).catch(() => undefined);
  };

  const handleTrackCase = (caseResult: CaseResult) => {
    logResultClick(caseResult);
    if (onTrackClick) {
      onTrackClick(caseResult, searchEventId);
      return;
    }
    const params = buildTrackCaseParams(caseResult, {
      indexedMatch: isIndexedMatch,
      searchEventId,
      logicalSearchId,
    });

    const trackUrl = `/track-case?${params.toString()}`;

    if (demoMode) {
      // Demo dashboard: preserve the exact case they chose. /track-case will
      // store the intent and bounce unpaid users to pricing with context.
      analyticsEvents.ctaClick('start_tracking_found_case', 'demo_search_result');
      router.push(trackUrl);
      return;
    }

    if (!isActuallyAuthenticated) {
      logSignInStarted();
      router.push(`/login?redirect=${encodeURIComponent(trackUrl)}`);
      return;
    }

    router.push(trackUrl);
  };

  // `attempt` supports a single automatic re-run on TRANSPORT failures only
  // (network drop, or a 5xx that died before any court-source outcome —
  // e.g. the Vercel 504 when the search exceeds the function budget).
  // Designed court-source outcomes (TIMEOUT / PARTIAL / SOURCE_UNAVAILABLE)
  // still require the explicit Try Again click — see the note at that button.
  const executeSearch = async (
    query: string,
    v2?: StructuredSearchRequest,
    opts?: { skipCandidates?: boolean },
    attempt = 1,
  ) => {
    setShowRetryButton(false);
    setShowSignInPrompt(false);
    setIsIndexedMatch(false);
    setCaseTypeRecovery(null);
    setCandidateEscapeState(null);
    setCaseYearRecoveryOpen(false);
    setRecoveryCaseYear('');
    lastQueryRef.current = query;
    lastV2RequestRef.current = v2;
    setIsLoading(true);
    // The candidate-chooser escape hatch re-runs the same journey with a wider
    // scope, so it reuses the id its original search already reported.
    if (!logicalSearchIdRef.current && opts?.skipCandidates && logicalSearchId) {
      logicalSearchIdRef.current = logicalSearchId;
    }
    if (!logicalSearchIdRef.current) {
      logicalSearchIdRef.current = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }

    try {
      const res = await fetch('/api/smart-finder', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildSmartFinderRequestBody({
          query,
          v2,
          logicalSearchId: logicalSearchIdRef.current,
          courtContext,
          sourceSurface: searchSurface || (demoMode ? 'demo_dashboard' : 'smart_case_finder'),
          sourceSlug: sourcePageSlug || searchSlug,
          skipCandidates: opts?.skipCandidates,
        })),
      });

      // A gateway 504/502 returns an HTML body — parse defensively so a
      // transport failure never surfaces as the generic "Connection error".
      let data: SmartFinderResponse;
      try {
        data = await res.json();
      } catch {
        data = {
          type: 'error',
          message: 'The connection dropped before the search could finish. No conclusion was reached — please try again.',
          retryable: true,
        } as SmartFinderResponse;
      }

      if (res.status >= 500 && attempt === 1) {
        setRetryNotice('The connection to the court source dropped — retrying your search automatically…');
        await new Promise((resolve) => setTimeout(resolve, 1500));
        // `return await` matters: with a bare `return`, this frame's `finally`
        // would run while attempt 2 is still in flight and kill its loading UI.
        return await executeSearch(query, v2, opts, 2);
      }

      setSearchEventId(data.searchEventId || null);
      setLogicalSearchId(data.logicalSearchId || logicalSearchIdRef.current);
      if (data.logicalSearchId) logicalSearchIdRef.current = data.logicalSearchId;
      setHasSearched(true);
      fetch('/api/search-funnel/event', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
        body: JSON.stringify({ event_name: 'response_received', search_event_id: data.searchEventId, logical_search_id: data.logicalSearchId }),
      }).catch(() => undefined);
      if (data.freeSearch) {
        setFreeSearch(data.freeSearch);
      }

      const code = data.code || (res.status === 401 ? 'AUTH_REQUIRED' : res.status === 429 ? 'RATE_LIMITED' : undefined);
      const normalizationMessage = data.normalizedQuery
        ? `We searched your text as “${data.normalizedQuery}”.`
        : '';
      const emptySearchAnalytics = classifyEmptySearchAnalytics(data);
      if (emptySearchAnalytics === 'search_no_results') {
        analyticsEvents.searchNoResults(query);
      } else if (emptySearchAnalytics === 'search_incomplete') {
        trackEvent('search_incomplete', {
          search_term: query,
          failure_code: data.failureCode || data.code || 'INCOMPLETE_COVERAGE',
          response_type: data.type,
        }, { metaCustom: false });
      }

      if (data.type === 'results' && data.cases && data.cases.length > 0) {
        const indexedMatch = isRecentIndexMatch(data);
        setIsIndexedMatch(indexedMatch);
        setResults(data.cases);
        setShowRetryButton(false);
        if (data.totalCount) setTotalCount(data.totalCount);
        setCorrectedMessage([normalizationMessage, data.correctedMessage].filter(Boolean).join(' '));
        setStatusMessage(
          data.cases.length === 1
            ? 'Case found'
            : `${data.cases.length} matching cases found`
        );
        const chooserNotice = candidateChooserNotice(data);
        if (chooserNotice) {
          setCorrectedMessage([normalizationMessage, chooserNotice.message].filter(Boolean).join(' '));
          setCandidateEscapeState({
            escape: chooserNotice.escape,
            caseNumber: data.parsed?.case_number || '',
            caseYear: data.parsed?.case_year || null,
            caseType: data.parsed?.case_type || null,
          });
        } else if (!indexedMatch && !data.partialCoverage && (code === 'PARTIAL' || code === 'INCOMPLETE' || data.searchError)) {
          setCorrectedMessage([
            normalizationMessage,
            data.searchError || 'Some court sources could not be checked. These results may be incomplete.',
          ].filter(Boolean).join(' '));
        }
        // Track successful search in GA4
        analyticsEvents.searchPerformed(input.trim(), data.cases.length, data.parsed?.search_strategy);
        fetch('/api/search-funnel/event', {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
          body: JSON.stringify({ event_name: 'results_rendered', search_event_id: data.searchEventId, logical_search_id: data.logicalSearchId }),
        }).catch(() => undefined);
      } else if (data.type === 'no_results') {
        if (isIncompleteSearchResponse(data)) {
          setMessageType('error');
          setStatusMessage([normalizationMessage, INCOMPLETE_SEARCH_MESSAGE].filter(Boolean).join('\n\n'));
          setShowRetryButton(true);
        } else {
          setMessageType('info');
          setStatusMessage([normalizationMessage, data.message || 'No records found for your search.'].filter(Boolean).join('\n\n'));
        }
      } else if (data.type === 'error') {
        setMessageType('error');
        const incompleteSearch = isIncompleteSearchResponse(data);
        const messages: Record<string, string> = {
          AUTH_REQUIRED: 'Sign in to search court records. Your search will be saved.',
          RATE_LIMITED: 'You have searched too often. Please wait a moment before trying again.',
          INVALID_REQUEST: 'Some search details were invalid. Please re-enter them and try again.',
          CAPACITY_BUSY: 'Search is at capacity right now. Please try again shortly.',
          TIMEOUT: 'The court source did not respond in time. No conclusion was reached; please try again.',
          SOURCE_UNAVAILABLE: 'The official court portal for this court is not responding right now. This is a temporary outage at the court source — please try the same search again in a few minutes.',
          PARTIAL: 'Only part of the requested court scope was checked. Refine your search or try again.',
          INCOMPLETE: 'The search could not check every requested source. This is not a confirmed no-result.',
        };
        setStatusMessage([
          normalizationMessage,
          incompleteSearch
            ? INCOMPLETE_SEARCH_MESSAGE
            : (code && messages[code]) || data.message || 'Court search could not be completed. Please try again.',
        ].filter(Boolean).join('\n\n'));
        setShowRetryButton(code !== 'AUTH_REQUIRED' && (incompleteSearch || Boolean(data.retryable) || ['RATE_LIMITED', 'CAPACITY_BUSY', 'TIMEOUT', 'SOURCE_UNAVAILABLE', 'PARTIAL', 'INCOMPLETE'].includes(code || '')));
        setShowSignInPrompt(
          !isActuallyAuthenticated &&
          (code === 'FREE_SEARCH_QUOTA_EXHAUSTED' || code === 'AUTH_REQUIRED')
        );
      } else if (data.type === 'clarification') {
        const recovery = caseTypeRecoveryFromResponse(data);
        if (recovery) {
          setCaseTypeRecovery(recovery);
          setStatusMessage([
            normalizationMessage,
            `Courts record case ${recovery.caseNumber}${recovery.caseYear ? `/${recovery.caseYear}` : ''} under a case type — the short letter code before the number on your court papers (like CS, WP or HMA).`,
          ].filter(Boolean).join('\n\n'));
        } else {
          setStatusMessage([
            normalizationMessage,
            data.message || SMART_FINDER_SEARCH_REQUIREMENTS,
          ].filter(Boolean).join('\n\n'));
        }
      } else {
        setStatusMessage([
          normalizationMessage,
          data.message || 'No records found for your search. Try a different search term.',
        ].filter(Boolean).join('\n\n'));
      }
      // A clarification keeps the journey open — the refined retry reuses the
      // same logical-search id. Any terminal outcome closes it so the next
      // query starts a fresh journey.
      if (data.type !== 'clarification') {
        logicalSearchIdRef.current = null;
      }
    } catch {
      if (attempt === 1) {
        setRetryNotice('Connection hiccup — retrying your search automatically…');
        await new Promise((resolve) => setTimeout(resolve, 1500));
        // `return await` matters — see the transport-retry branch above.
        return await executeSearch(query, v2, opts, 2);
      }
      setMessageType('error');
      setStatusMessage('Connection error. Please check your internet and try again.');
      setShowRetryButton(true);
    } finally {
      setIsLoading(false);
      setRetryNotice('');
      inputRef.current?.focus();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const query = input.trim();
    if (!query || isLoading) return;

    // Anonymous visitors search directly — the API applies a per-IP daily
    // allowance and returns masked results; sign-in is asked for at track time.
    if (searchSlug) {
      analyticsEvents.seoLandingSearchSubmitted('smart', searchSlug, searchSurface || 'location_page');
    }

    setResults([]);
    setStatusMessage('');
    setCorrectedMessage('');
    setTotalCount(null);
    setSearchEventId(null);
    setLogicalSearchId(null);
    setMessageType('info');

    await executeSearch(query);
  };

  const handleV2Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    const originalInput = intent === 'cnr'
      ? input.trim()
      : intent === 'case_number'
        ? [caseType.trim(), caseNumber.trim(), caseYear.trim()].filter(Boolean).join('/')
        : partyName.trim();
    const v2: StructuredSearchRequest = {
      intent,
      originalInput,
      ...(intent === 'cnr' ? { cnr: input.trim() } : {}),
      ...(intent === 'case_number' ? { caseType: caseType.trim(), caseNumber: caseNumber.trim(), caseYear: caseYear.trim() } : {}),
      ...(intent === 'party_name' ? { partyName: partyName.trim(), ...(caseYear.trim() ? { caseYear: caseYear.trim() } : {}) } : {}),
      ...(sourcePageSlug ? { sourcePageSlug } : {}),
      ...(requiresCourtLocation && intent !== 'cnr'
        ? { courtTierSelection: 'district' as const }
        : needsExplicitTier && allCourtsTier
          ? { courtTierSelection: allCourtsTier }
          : {}),
      ...((!sourcePageSlug || requiresCourtLocation) && intent !== 'cnr' ? { courtLocation: courtLocation.trim() } : {}),
      ...(searchSurface ? { sourceSurface: searchSurface } : {}),
    };
    // Missing fields stay in the inline form and do not reach quota/provider
    // admission. The server repeats validation for direct API callers.
    const missing = !originalInput ||
      (intent === 'case_number' && (!caseType.trim() || !caseNumber.trim() || !caseYear.trim())) ||
      (intent === 'party_name' && !partyName.trim()) ||
      (needsExplicitTier && !allCourtsTier) ||
      ((!sourcePageSlug || requiresCourtLocation) && intent !== 'cnr' && !courtLocation.trim());
    if (missing) {
      setResults([]);
      setMessageType('info');
      setStatusMessage(needsExplicitTier && !allCourtsTier
        ? 'Choose High Court or District Court before searching.'
        : intent === 'case_number'
          ? sourcePageSlug && !requiresCourtLocation
            ? 'Enter the case type, case number, and year.'
            : 'Enter the case type, case number, year, and court or city.'
          : intent === 'party_name'
            ? sourcePageSlug && !requiresCourtLocation
              ? 'Enter a party name.'
              : 'Enter a party name and court or city.'
            : 'Enter the requested case details.');
      return;
    }
    setResults([]);
    setStatusMessage('');
    setCorrectedMessage('');
    setTotalCount(null);
    setSearchEventId(null);
    setMessageType('info');
    if (searchSlug) analyticsEvents.seoLandingSearchSubmitted('smart', searchSlug, searchSurface || 'location_page');
    await executeSearch(originalInput, v2);
  };

  const handleClear = () => {
    setInput('');
    setResults([]);
    setStatusMessage('');
    setCorrectedMessage('');
    setTotalCount(null);
    setSearchEventId(null);
    setMessageType('info');
    setShowRetryButton(false);
    setShowSignInPrompt(false);
    setCandidateEscapeState(null);
    setCaseYearRecoveryOpen(false);
    setRecoveryCaseYear('');
    logicalSearchIdRef.current = null;
    inputRef.current?.focus();
  };

  const handleRetry = () => {
    const query = lastQueryRef.current || input.trim();
    if (!query || isLoading) return;
    setResults([]);
    setStatusMessage('');
    setCorrectedMessage('');
    setTotalCount(null);
    setMessageType('info');
    setShowRetryButton(false);
    executeSearch(query, lastV2RequestRef.current);
  };

  const runRecoverySearch = (query: string, ctaName: string) => {
    if (!query || isLoading) return;
    analyticsEvents.ctaClick(ctaName, searchSurface || 'smart_case_finder');
    setInput(query);
    setResults([]);
    setStatusMessage('');
    setCorrectedMessage('');
    setTotalCount(null);
    setMessageType('info');
    executeSearch(query);
  };

  const handleCaseTypeRetry = (e: React.FormEvent) => {
    e.preventDefault();
    if (!caseTypeRecovery) return;
    const type = recoveryCaseType.trim().toUpperCase();
    if (!type) return;
    const query = [type, caseTypeRecovery.caseNumber, caseTypeRecovery.caseYear].filter(Boolean).join('/');
    runRecoverySearch(query, 'case_type_recovery_with_type');
  };

  const handleCandidateFullSearchEscape = () => {
    const query = lastQueryRef.current;
    if (!query || isLoading) return;
    analyticsEvents.ctaClick('candidate_chooser_full_search', searchSurface || 'smart_case_finder');
    executeSearch(query, lastV2RequestRef.current, { skipCandidates: true });
  };

  const handleCaseYearRetry = (e: React.FormEvent) => {
    e.preventDefault();
    if (!candidateEscapeState) return;
    const year = recoveryCaseYear.trim();
    if (!/^\d{4}$/.test(year)) return;
    // A fully-specified TYPE/NUMBER/YEAR query searches that exact identity
    // live — no candidate nomination, no year guessing.
    const query = [candidateEscapeState.caseType, candidateEscapeState.caseNumber, year]
      .filter(Boolean)
      .join('/');
    runRecoverySearch(query, 'candidate_chooser_with_year');
  };

  const handlePartyNameRetry = (e: React.FormEvent) => {
    e.preventDefault();
    if (!caseTypeRecovery) return;
    const name = recoveryPartyName.trim();
    if (!name) return;
    // The filing year from their own case reference keeps High Court party
    // searches past the year gate and narrows district matches.
    const query = [name, caseTypeRecovery.caseYear].filter(Boolean).join(' ');
    runRecoverySearch(query, 'case_type_recovery_by_name');
  };

  // Shared across the no-results status panel and the candidate-chooser
  // escape hatch under a rendered results list — same recovery UI either way.
  const caseTypeRecoveryPanel = caseTypeRecovery ? (
    <div className="mt-4 mx-auto max-w-md space-y-4 rounded-xl border border-primary-100 bg-white p-4 text-left shadow-sm">
      <form onSubmit={handleCaseTypeRetry} aria-label="Search again with the case type">
        <p className="mb-2 text-sm font-medium text-gray-800">
          Have your court papers? Add the case type:
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            value={recoveryCaseType}
            onChange={(e) => setRecoveryCaseType(e.target.value)}
            placeholder="Case type (e.g. CS)"
            aria-label="Case type"
            autoComplete="off"
            disabled={isLoading}
            className="min-h-11 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm uppercase text-gray-900 placeholder:normal-case placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
          />
          <button
            type="submit"
            disabled={isLoading || !recoveryCaseType.trim()}
            className="min-h-11 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Search {caseTypeRecovery.caseNumber}{caseTypeRecovery.caseYear ? `/${caseTypeRecovery.caseYear}` : ''}
          </button>
        </div>
      </form>
      <form onSubmit={handlePartyNameRetry} className="border-t border-gray-100 pt-3" aria-label="Search by party name instead">
        <p className="mb-2 text-sm font-medium text-gray-800">
          Don&apos;t know the case type? Search with a name from the case instead:
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            value={recoveryPartyName}
            onChange={(e) => setRecoveryPartyName(e.target.value)}
            placeholder="Party name (e.g. your full name)"
            aria-label="Party name"
            autoComplete="off"
            disabled={isLoading}
            className="min-h-11 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
          />
          <button
            type="submit"
            disabled={isLoading || !recoveryPartyName.trim()}
            className="min-h-11 rounded-lg border border-primary bg-white px-4 py-2 text-sm font-semibold text-primary transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Search by name
          </button>
        </div>
      </form>
    </div>
  ) : null;

  // Escape hatch for yearless candidate results: the visitor already gave the
  // case type, so the missing datum is the filing year. Submitting re-runs the
  // search as an exact TYPE/NUMBER/YEAR lookup.
  const caseYearRecoveryPanel = candidateEscapeState && caseYearRecoveryOpen ? (
    <div className="mt-3 mx-auto max-w-md rounded-xl border border-primary-100 bg-white p-4 text-left shadow-sm">
      <form onSubmit={handleCaseYearRetry} aria-label="Search again with the filing year">
        <p className="mb-2 text-sm font-medium text-gray-800">
          Which year was the case filed?
        </p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            value={recoveryCaseYear}
            onChange={(e) => setRecoveryCaseYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
            placeholder="Filing year (e.g. 2025)"
            aria-label="Filing year"
            inputMode="numeric"
            autoComplete="off"
            disabled={isLoading}
            className="min-h-11 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
          />
          <button
            type="submit"
            disabled={isLoading || !/^\d{4}$/.test(recoveryCaseYear.trim())}
            className="min-h-11 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Search {[candidateEscapeState.caseType, candidateEscapeState.caseNumber].filter(Boolean).join('/')}/{/^\d{4}$/.test(recoveryCaseYear.trim()) ? recoveryCaseYear.trim() : 'year'}
          </button>
        </div>
      </form>
    </div>
  ) : null;

  return (
    <div className="w-full max-w-2xl mx-auto mt-2">
      {(demoMode || !isActuallyAuthenticated) && quotaLabel && (
        <div className="mb-3 flex justify-center">
          <span className={`inline-flex items-center rounded-md border px-3 py-1 text-xs font-semibold ${
            freeSearch?.remaining === 0
              ? 'border-amber-200 bg-amber-50 text-amber-800'
              : 'border-primary-100 bg-white text-primary'
          }`}>
            {quotaLabel}
          </span>
        </div>
      )}

      {/* Search panel */}
      <div className="relative">
        {searchV2Enabled ? (
          <form onSubmit={handleV2Submit} className="rounded-2xl border-2 border-primary-100 bg-white p-3 shadow-sm sm:p-4" aria-label="Find a case">
            <SearchMethodSelector
              value={intent}
              onChange={setIntent}
              modes={searchV2Modes}
              disabled={isLoading}
            />
            {pageScopeLabel && pageCourtType !== 'All Courts' && (
              <p className="mb-3 rounded-lg border border-primary-100 bg-primary-50 px-3 py-2 text-xs font-medium text-primary-800">
                Searching: {pageScopeLabel}
              </p>
            )}
            {needsExplicitTier && (
              <fieldset className="mb-3" aria-label="Choose court tier">
                <legend className="mb-1 text-xs font-semibold text-gray-700">Which court do you want to search?</legend>
                <div className="flex gap-2">
                  {(['high_court', 'district'] as const).map((tier) => (
                    <button key={tier} type="button" onClick={() => setAllCourtsTier(tier)} aria-pressed={allCourtsTier === tier}
                      className={`min-h-11 flex-1 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${allCourtsTier === tier ? 'border-primary bg-primary text-white shadow-sm' : 'border-gray-300 bg-white text-gray-700 hover:border-primary-300 hover:bg-primary-50'}`}>
                      {allCourtsTier === tier ? 'Selected: ' : ''}{tier === 'high_court' ? 'High Court' : 'District Court'}
                    </button>
                  ))}
                </div>
                {allCourtsTier && (
                  <p className="mt-2 text-xs font-medium text-primary-800" role="status">
                    Searching {pageLocationLabel || courtLocation || 'this location'} {allCourtsTier === 'high_court' ? 'High Court' : 'District Courts'} only.
                  </p>
                )}
              </fieldset>
            )}
            {requiresCourtLocation && intent !== 'cnr' && (
              <p className="mb-3 rounded-lg border border-primary-100 bg-primary-50 px-3 py-2 text-xs font-medium text-primary-800">
                Enter the city. We will search its district courts; you do not need to know the court complex.
              </p>
            )}
            {pageCourtType === 'All Courts' && intent === 'cnr' && (
              <p className="mb-2 text-xs font-medium text-primary-800">
                A CNR identifies the exact court automatically.
              </p>
            )}
            <div className="grid gap-2 sm:grid-cols-3">
              {intent === 'cnr' && (
                <label className="sm:col-span-3">
                  <span className="sr-only">CNR number</span>
                  <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} disabled={isLoading} autoComplete="off"
                    placeholder="16-character CNR number" className="w-full rounded-xl border border-gray-300 px-3 py-3 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" />
                </label>
              )}
              {intent === 'case_number' && <>
                <input value={caseType} onChange={(e) => setCaseType(e.target.value)} disabled={isLoading} placeholder="Case type (e.g. WP)" aria-label="Case type" className="rounded-xl border border-gray-300 bg-white px-3 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" />
                <input value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} disabled={isLoading} placeholder="Case number" inputMode="numeric" aria-label="Case number" className="rounded-xl border border-gray-300 bg-white px-3 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" />
                <input value={caseYear} onChange={(e) => setCaseYear(e.target.value)} disabled={isLoading} placeholder="Year" inputMode="numeric" aria-label="Case filing year" className="rounded-xl border border-gray-300 bg-white px-3 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" />
              </>}
              {intent === 'party_name' && <>
                <input value={partyName} onChange={(e) => setPartyName(e.target.value)} disabled={isLoading} placeholder="Party name (Hindi or a major Indian script)" aria-label="Party name" className="sm:col-span-2 rounded-xl border border-gray-300 bg-white px-3 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" />
                <input value={caseYear} onChange={(e) => setCaseYear(e.target.value)} disabled={isLoading} placeholder="Year (required for High Court)" inputMode="numeric" aria-label="Case filing year" className="rounded-xl border border-gray-300 bg-white px-3 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" />
              </>}
              {(!sourcePageSlug || requiresCourtLocation) && intent !== 'cnr' && (
                <input value={courtLocation} onChange={(e) => setCourtLocation(e.target.value)} disabled={isLoading} placeholder="Court or city (English or your language)" aria-label="Court or location" className="sm:col-span-3 rounded-xl border border-gray-300 bg-white px-3 py-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15" />
              )}
            </div>
            <button type="submit" disabled={isLoading} className="mt-3 inline-flex min-h-11 w-full items-center justify-center rounded-xl bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-50">
              {isLoading ? 'Searching official court records…' : 'Search official court records'}
            </button>
            <p className="mt-2 text-center text-xs leading-5 text-gray-500">
              {!sourcePageSlug || requiresCourtLocation
                ? SMART_FINDER_SEARCH_REQUIREMENTS
                : 'This page supplies the court. Enter the case type, number, and filing year, or enter the party name.'}
            </p>
          </form>
        ) : (
        <>
        {pageScopeLabel && !requiresCourtLocation && (
          <p className="mb-2 rounded-lg border border-primary-100 bg-primary-50 px-3 py-2 text-xs font-medium text-primary-800">
            {pageCourtType === 'All Courts'
              ? `Searching High Court and district courts across ${pageLocationLabel || 'this location'}.`
              : `Searching ${pageScopeLabel}.`}
          </p>
        )}
        {requiresCourtLocation && (
          <p className="mb-2 rounded-lg border border-primary-100 bg-primary-50 px-3 py-2 text-xs font-medium text-primary-800">
            Include the city in your search. We will check its supported district courts.
          </p>
        )}
        <form onSubmit={handleSubmit} className="relative">
          <div className="relative flex items-center group">
            {/* Search icon */}
            <div className="absolute left-3.5 text-primary-400 pointer-events-none transition-colors group-focus-within:text-primary">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={hasSearched ? 'Search another case...' : (placeholder || SMART_FINDER_EXAMPLE_QUERIES[activeExample])}
              className="w-full pl-11 pr-24 py-3.5 bg-white border-2 border-primary-100 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-primary focus:shadow-[0_0_0_3px_rgba(30,58,95,0.06)] transition-all"
              disabled={isLoading}
              autoComplete="off"
            />

            {/* Right side buttons */}
            <div className="absolute right-2 flex items-center gap-1">
              {input && !isLoading && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
                  aria-label="Clear"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
              <button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="bg-primary text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-primary-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {isLoading ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    <span className="hidden sm:inline">Searching</span>
                  </>
                ) : (
                  <span>Search</span>
                )}
              </button>
            </div>
          </div>
        </form>
        <p className="mt-2 text-xs leading-5 text-gray-500">
          {sourcePageSlug && !requiresCourtLocation
            ? 'This page supplies the court. Enter the case type, number, and filing year, or enter the party name.'
            : SMART_FINDER_SEARCH_REQUIREMENTS}
        </p>
        </>
        )}

        {/* Search tips (before first search) — same one-tap interaction as /add-case. */}
        {!searchV2Enabled && !hasSearched && results.length === 0 && !isLoading && (
          <div className="mt-4 flex flex-wrap gap-2">
            {quickSearchTips.map((tip) => (
              <button
                key={tip.label}
                type="button"
                onClick={() => {
                  setInput(tip.example);
                  inputRef.current?.focus();
                }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-primary-100 bg-primary-50 px-3 py-1.5 text-xs text-primary-600 transition-colors hover:bg-primary-100"
              >
                <span className="font-medium">{tip.label}</span>
                <span className="font-mono text-primary-400">{tip.example}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Status / Loading indicator — feedbackRef keeps this visible via the
          auto-scroll effect (hero surfaces render it below the fold). */}
      <div ref={feedbackRef}>
      {isLoading && <SearchProgress notice={retryNotice} />}

      {/* Status message (no results / clarification / error) */}
      {!isLoading && statusMessage && results.length === 0 && (
        <div
          className="mt-6 text-center"
          role={messageType === 'error' ? 'alert' : 'status'}
          aria-live={messageType === 'error' ? 'assertive' : 'polite'}
          aria-atomic="true"
        >
          <div className={`inline-flex items-start gap-2 text-sm px-4 py-2.5 rounded-xl border ${
            messageType === 'error'
              ? 'bg-orange-50 text-orange-800 border-orange-200'
              : 'bg-primary-50 text-primary-700 border-primary-100'
          }`}>
            {messageType === 'error' ? (
              <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            ) : (
              <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            )}
            <span className="text-left whitespace-pre-line">{statusMessage}</span>
          </div>
          {/* Designed court-source outcomes (TIMEOUT / PARTIAL / SOURCE_UNAVAILABLE)
              keep retry as an explicit user action so clients never multiply
              portal work. Only TRANSPORT failures — network drop or a 5xx that
              died before any outcome — auto-retry once, in executeSearch. */}
          {showRetryButton && (
            <button
              onClick={handleRetry}
              className="mt-3 inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Try Again
            </button>
          )}
          {showSignInPrompt && (
            <div className="mt-3">
              <button
                onClick={() => {
                  const returnUrl = `/add-case?query=${encodeURIComponent(lastQueryRef.current)}`;
                  router.push(`/login?redirect=${encodeURIComponent(returnUrl)}`);
                }}
                className="inline-flex items-center gap-2 bg-primary text-white px-5 py-2.5 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors"
              >
                Sign in to continue
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>
          )}
          {/* Case-type dead-end recovery: a type input for visitors with court
              papers, and a party-name pivot for litigants who never learn the
              type codes. Both re-run the search inside the same page scope. */}
          {caseTypeRecoveryPanel}
        </div>
      )}
      </div>

      {/* Results */}
      {results.length > 0 && (
        <div ref={resultsRef} className="mt-6 space-y-3">
          {/* Results limit notice — only meaningful for auth users (non-auth
              users only see the first card and a sign-in CTA card below). */}
              {(showFullResults || showLimitedPreview) && totalCount && totalCount > results.length && (
            <div className="flex items-center gap-2 text-sm px-4 py-2.5 rounded-xl bg-blue-50 text-blue-800 border border-blue-200">
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Showing top {results.length} of {totalCount} results. Add more details (case year, case type, bench) to narrow your search.</span>
            </div>
          )}

          {/* Friendly index-coverage notice */}
          {isIndexedMatch && (
            <div className="flex items-start gap-2 rounded-xl border border-primary-100 bg-primary-50 px-4 py-3 text-sm text-primary-800" role="status">
              <svg className="mt-0.5 h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{showFullResults ? AUTHENTICATED_INDEX_MATCH_NOTICE : INDEX_MATCH_NOTICE}</span>
            </div>
          )}

          {/* Spelling correction or non-index partial-provider notice */}
          {correctedMessage && (
            <div className="flex items-center gap-2 text-sm px-4 py-2.5 rounded-xl bg-amber-50 text-amber-800 border border-amber-200" role="status" aria-live="polite">
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{correctedMessage}</span>
            </div>
          )}

          {/* Results header */}
          <div className="flex items-center justify-between px-1" role="status" aria-live="polite">
            <span className="text-xs font-semibold text-primary-500 uppercase tracking-wider">
              {statusMessage}
            </span>
            <button
              onClick={handleClear}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              Clear results
            </button>
          </div>

          {showLimitedPreview && (
            <div className="flex items-start gap-2 text-sm px-4 py-3 rounded-xl bg-primary-50 text-primary-800 border border-primary-100">
              <svg className="w-4 h-4 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>We found your case. Add it to your dashboard using your free case slot, or choose a plan when you need to track more matters.</span>
            </div>
          )}

          {/* Result cards.
              - Authenticated users see every result card.
              - Non-auth users see the first two cards (with details masked
                inside CaseResultCard) followed by a single "more cases" sign-in
                card. This both reinforces "yes, your case is here" and gates
                the rest of the list behind sign-in. */}
          {showFullResults || showLimitedPreview
            ? results.map((c, i) => (
                <CaseResultCard
                  key={c.identity_id || c.cnr_number || `${c.case_type || 'case'}:${c.case_number || i}:${c.case_year || ''}`}
                  caseResult={c}
                  onTrack={() => handleTrackCase(c)}
                  isAuthenticated={showFullResults}
                  isLimitedPreview={showLimitedPreview}
                  isIndexedMatch={isIndexedMatch}
                  ctaLabel={onTrackClick ? 'Start WhatsApp Tracking' : showLimitedPreview ? 'Start tracking this case' : undefined}
                />
              ))
            : (
              <>
                {results.slice(0, PUBLIC_RESULT_PREVIEW_COUNT).map((c, i) => (
                  <CaseResultCard
                    key={c.identity_id || c.cnr_number || `${c.case_type || 'case'}:${c.case_number || i}:${c.case_year || ''}`}
                    caseResult={c}
                    onTrack={() => handleTrackCase(c)}
                    isAuthenticated={false}
                    isIndexedMatch={isIndexedMatch}
                    // Candidate results can come from a different filing year
                    // than the visitor meant; keep the year readable so they
                    // can tell whether this is their case before tracking.
                    revealFilingYear={Boolean(candidateEscapeState)}
                    ctaLabel={onTrackClick ? 'Start WhatsApp Tracking' : demoMode ? 'Upgrade to see your case' : undefined}
                  />
                ))}
                {(() => {
                  const previewCount = Math.min(PUBLIC_RESULT_PREVIEW_COUNT, results.length);
                  const moreCount = (totalCount ?? results.length) - previewCount;
                  if (moreCount <= 0) return null;
                  return (
                    <div className="bg-white border border-primary-200 rounded-xl p-5 text-center">
                      <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-primary-50 flex items-center justify-center">
                        <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m0 0v2m0-2h2m-2 0h-2m9-7a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <p className="text-base font-semibold text-gray-900 mb-1">
                        We found {moreCount} more matching {moreCount === 1 ? 'case' : 'cases'}
                      </p>
                      <p className="text-sm text-gray-600 mb-4 max-w-md mx-auto">
                        {demoMode
                          ? 'Upgrade to view full details, start tracking, and get hearing alerts.'
                          : 'Sign in to view full case details and start tracking. Hearing dates, orders and notifications included.'}
                      </p>
                      <button
                        onClick={() => handleTrackCase(results[0])}
                        className="inline-flex items-center gap-2 bg-primary text-white text-sm font-semibold px-6 py-2.5 rounded-xl hover:bg-primary-700 active:scale-[0.99] transition-all"
                      >
                        {demoMode ? 'Upgrade to see your case' : 'Sign in to view all'}
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                        </svg>
                      </button>
                    </div>
                  );
                })()}
              </>
            )
          }

          {/* Candidate-chooser escape hatch: these results came from stored-record
              candidates verified live, never an exhaustive search, so give the
              visitor a way out when their case isn't among them. */}
          {candidateEscapeState && (
            <p className="px-1 text-sm text-gray-500">
              {candidateEscapeState.escape === 'case_type' ? (
                <>
                  Not seeing your case?{' '}
                  <button
                    type="button"
                    onClick={() => setCaseTypeRecovery({
                      caseNumber: candidateEscapeState.caseNumber,
                      caseYear: candidateEscapeState.caseYear,
                    })}
                    className="font-semibold text-primary underline-offset-2 hover:underline"
                  >
                    Add your case type
                  </button>
                </>
              ) : candidateEscapeState.escape === 'case_year' ? (
                <>
                  Not seeing your case?{' '}
                  <button
                    type="button"
                    onClick={() => setCaseYearRecoveryOpen(true)}
                    className="font-semibold text-primary underline-offset-2 hover:underline"
                  >
                    Add the filing year
                  </button>
                </>
              ) : (
                <>
                  Not your case?{' '}
                  <button
                    type="button"
                    onClick={handleCandidateFullSearchEscape}
                    disabled={isLoading}
                    className="font-semibold text-primary underline-offset-2 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Search the full court records
                  </button>
                </>
              )}
            </p>
          )}
          {candidateEscapeState?.escape === 'case_type' && caseTypeRecoveryPanel}
          {candidateEscapeState?.escape === 'case_year' && caseYearRecoveryPanel}
        </div>
      )}
    </div>
  );
}
